import {MarkdownWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {useMarkdownWidgetUpdate} from "./useMarkdownWidgetUpdate"
import {useAutoSave} from "../../hooks/useAutoSave"
import './MarkdownWidgetFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: MarkdownWidgetData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const MarkdownWidgetFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const [editingText, setEditingText] = useState(data.text)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text)
    }, [data.text])

    const {updateData} = useMarkdownWidgetUpdate({canvasId, widgetId})

    const saveText = () => {
        if (editingText !== data.text) {
            updateData(['text'], {...data, text: editingText})
        }
    }

    useAutoSave({saveFn: saveText})

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }

    const handleCompactToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const newCompact = !data.compact
        updateData(['compact'], {...data, compact: newCompact})
    }

    return (
        <div className='markdown-widget-full-editor'>
            <div className='markdown-widget-full-editor__header'>
                <span className='markdown-widget-full-editor__title'>Markdown Widget Properties</span>
                <button
                    className='markdown-widget-full-editor__close'
                    onClick={onClose}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='markdown-widget-full-editor__sections'>
                <div className='markdown-widget-full-editor__section markdown-widget-full-editor__section--compact' title='Compact'>
                    <button
                        className={`markdown-widget-full-editor__button ${data.compact ? 'markdown-widget-full-editor__button--active' : ''}`}
                        onMouseDown={handleCompactToggle}
                        aria-label='Toggle compact mode'
                        type='button'
                    >
                        Compact
                    </button>
                </div>

                <div className='markdown-widget-full-editor__section markdown-widget-full-editor__section--text' title='Markdown'>
                    <textarea
                        ref={textareaRef}
                        className='markdown-widget-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        autoFocus
                        rows={10}
                    />
                </div>
            </div>
        </div>
    )
}
